import { Suspense, useState } from "react";
import { Link, useLocation, type To } from "react-router";
import {
  BadgeQuestionMark,
  BookOpen,
  Hand,
  Library,
  Lock,
  LockOpen,
  LogOut,
  type LucideIcon,
  MessageSquare,
  MonitorPlay,
  Pencil,
  SlidersHorizontal,
  SmilePlus,
  Sparkles,
  Upload,
} from "lucide-react";
import Text from "../../primitives/components/Text/Text";
import { MODELS, useModel } from "../../../api";
import { ROUTES } from "../../../routes.js";
import type { SmallTeamForUser } from "../../../api/smallTeam";
import type { WorkshopId } from "../../../api/workshop";
import { ClassMessageDialog } from "../../../features/workshops/classroom-management/ClassMessageDialog";
import { PauseDialog } from "../../../features/workshops/classroom-management/PauseDialog";
import { useLiveState } from "../../../features/workshops/classroom-management/hooks/useLiveState";
import { ToolbarMenu, ToolbarMenuItem } from "./ToolbarMenu";
import styles from "./toolbar.css";

const icons = {
  library: Library,
  monitorPlay: MonitorPlay,
  bookOpen: BookOpen,
  smilePlus: SmilePlus,
  slidersHorizontal: SlidersHorizontal,
  logOut: LogOut,
  pencil: Pencil,
  upload: Upload,
} as const satisfies Record<string, LucideIcon>;

type ToolbarButtonProps = {
  to: To;
  active: boolean;
  iconName: keyof typeof icons;
  label: string;
  id?: string;
  target?: string;
  onClick?: () => void;
};

// Where the handbook lives until the in-app one replaces it: another site, so
// it opens in its own tab.
const EXTERNAL_HANDBOOK_URL = "https://www.digital-sparks.org/handbuch";

/**
 * Default `to`/`active` for a button that lives at `${baseUrl}/${segment}` and
 * is active whenever the path starts with `${baseUrl}/${activeSegment ?? segment}`.
 */
const toolbarLinkProps = (
  baseUrl: string,
  pathname: string,
  segment: string,
  activeSegment?: string,
): { to: string; active: boolean } => ({
  to: `${baseUrl}/${segment}`,
  active: pathname.startsWith(`${baseUrl}/${activeSegment ?? segment}`),
});

const ToolbarButton = ({
  to,
  active,
  iconName,
  label,
  id,
  target,
  onClick,
}: ToolbarButtonProps) => {
  const IconComponent = icons[iconName];
  return (
    <Link
      id={id}
      className={styles.toolbarButton({
        active,
      })}
      to={to}
      target={target}
      onClick={onClick}
    >
      <IconComponent size={24} />
      <Text variant="label" color="neutral-0">
        {label}
      </Text>
    </Link>
  );
};

type ToolbarFeatures = {
  library?: boolean;
  glossary?: boolean;
  isHandbookEnabled?: boolean;
};

/**
 * Teachers' entry point to the spark's content tools. Students reach the same
 * pages from top-level buttons — the teacher toolbar has to fit the handbook,
 * classroom management and options too, so these three move behind one trigger.
 *
 * The trigger highlights while any of its pages is open, so the toolbar still
 * shows where you are once the menu is closed.
 */
const SparkMenu = ({
  baseUrl,
  pathname,
  features,
  forceOpen,
}: {
  baseUrl: string;
  pathname: string;
  features: ToolbarFeatures;
  forceOpen?: boolean;
}) => {
  const library = toolbarLinkProps(baseUrl, pathname, "library");
  const glossary = toolbarLinkProps(baseUrl, pathname, "glossary");
  const showroom = toolbarLinkProps(baseUrl, pathname, "showroom");

  return (
    <ToolbarMenu
      id="spark-button"
      contentId="spark-menu"
      icon={Sparkles}
      label="Spark"
      active={library.active || glossary.active || showroom.active}
      forceOpen={forceOpen}
    >
      {features.library && (
        <ToolbarMenuItem icon={Library} label="Bibliothek" {...library} />
      )}
      {features.glossary && (
        <ToolbarMenuItem
          icon={BadgeQuestionMark}
          label="Glossar"
          {...glossary}
        />
      )}
      <ToolbarMenuItem icon={MonitorPlay} label="Showroom" {...showroom} />
    </ToolbarMenu>
  );
};

/**
 * Teacher/admin menu for everything that concerns the class itself: the live
 * actions (pause the class / show a message) and inviting people in.
 *
 * The trigger shows its active state while a pause is live, so teachers can see
 * at a glance that screens are locked. A message is fire-and-forget — there's no
 * lasting state to reflect — so it doesn't drive the active state. Both live
 * actions open a dialog; see PauseDialog / ClassMessageDialog for what they do.
 */
const ClassroomMenu = ({
  workshopId,
  invite,
}: {
  workshopId: WorkshopId;
  invite: { to: string; active: boolean };
}) => {
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const { pause } = useLiveState(workshopId);
  const paused = Boolean(pause);

  return (
    <>
      <ToolbarMenu
        id="classroom-button"
        icon={Hand}
        label="Klasse"
        active={paused}
      >
        <ToolbarMenuItem
          icon={paused ? Lock : LockOpen}
          label="Pausieren"
          active={paused}
          onClick={() => setPauseDialogOpen(true)}
        />
        <ToolbarMenuItem
          icon={MessageSquare}
          label="Nachricht"
          onClick={() => setMessageDialogOpen(true)}
        />
        <ToolbarMenuItem icon={SmilePlus} label="Einladen" {...invite} />
      </ToolbarMenu>

      <PauseDialog
        workshopId={workshopId}
        paused={paused}
        open={pauseDialogOpen}
        setOpen={setPauseDialogOpen}
      />
      <ClassMessageDialog
        workshopId={workshopId}
        open={messageDialogOpen}
        setOpen={setMessageDialogOpen}
      />
    </>
  );
};

const UploadButton = ({
  studentSmallTeam,
  pathname,
  baseUrl,
}: {
  studentSmallTeam: SmallTeamForUser;
  pathname: string;
  baseUrl: string;
}) => {
  const showroomData = useModel(
    MODELS.SMALL_TEAM_SHOWROOM_ENTRY(studentSmallTeam.id),
  );
  return (
    <ToolbarButton
      {...toolbarLinkProps(
        baseUrl,
        pathname,
        `team-diary/${studentSmallTeam.id}`,
        "team-diary",
      )}
      iconName={showroomData.entry ? "pencil" : "upload"}
      label={showroomData.entry ? "Mein Beitrag" : "Hochladen"}
    />
  );
};

type ToolbarProps = {
  mode: "student" | "teacher";
  features?: ToolbarFeatures;
  smallTeam?: SmallTeamForUser;
  /** Required for classroom management — without it the menu can't go live. */
  workshopId?: WorkshopId;
  /** Which URL the Toolbar is displayed on. */
  baseUrl: string;
  /** Which URL to navigate to on clicking leave. */
  leaveTo?: string;
  /** When set, admin actions are disabled and call this instead. */
  onRestrictedClick?: () => void;
  /**
   * Holds the Spark flyout open — the guided tour points a step at its entries.
   * Teacher mode only; students reach the same pages from top-level buttons.
   */
  sparkMenuOpen?: boolean;
};

export const Toolbar = ({
  mode,
  features = {},
  smallTeam,
  workshopId,
  baseUrl,
  leaveTo = "..",
  onRestrictedClick,
  sparkMenuOpen,
}: ToolbarProps) => {
  const { pathname } = useLocation();

  const isTeacher = mode === "teacher";
  // Only students with a small team get the upload button — and it's the team
  // whose showroom entry decides whether it reads "Hochladen" or "Mein Beitrag".
  const studentSmallTeam = isTeacher ? undefined : smallTeam;

  // Admin actions are links normally; in restricted mode (demo) they stay
  // visible but lead to the handler instead of navigating. An empty `to`
  // object resolves to the current location, so the link goes nowhere.
  const restrictedProps: { to: To; onClick: () => void } | null =
    onRestrictedClick ? { to: {}, onClick: onRestrictedClick } : null;

  // Inviting lives inside the Klassenzimmer menu where there is one. Restricted
  // mode has no live workshop, so it always takes the top-level fallback — which
  // is also the only branch that can carry `restrictedProps`.
  const invite = toolbarLinkProps(baseUrl, pathname, "invite");
  const classroomWorkshopId = isTeacher ? workshopId : undefined;

  const options = toolbarLinkProps(baseUrl, pathname, "options");

  return (
    <nav aria-label="Toolbar" className={styles.toolbarBox} id="toolbar">
      {isTeacher ? (
        <SparkMenu
          baseUrl={baseUrl}
          pathname={pathname}
          features={features}
          forceOpen={sparkMenuOpen}
        />
      ) : (
        <>
          {features.library && (
            <ToolbarButton
              id="library-button"
              {...toolbarLinkProps(baseUrl, pathname, "library")}
              iconName="library"
              label="Bibliothek"
            />
          )}
          <ToolbarButton
            id="showroom-button"
            {...toolbarLinkProps(baseUrl, pathname, "showroom")}
            iconName="monitorPlay"
            label="Showroom"
          />
        </>
      )}

      {isTeacher && (
        <>
          {classroomWorkshopId ? (
            <ClassroomMenu workshopId={classroomWorkshopId} invite={invite} />
          ) : (
            <ToolbarButton
              id="group-button"
              {...invite}
              {...restrictedProps}
              iconName="smilePlus"
              label="Einladen"
            />
          )}

          <ToolbarButton
            id="options-button"
            {...options}
            {...restrictedProps}
            iconName="slidersHorizontal"
            label="Optionen"
          />

          <ToolbarButton
            id="handbook-button"
            {...(features.isHandbookEnabled && ROUTES.handbook
              ? { to: ROUTES.handbook.getUrl(), active: false }
              : { to: EXTERNAL_HANDBOOK_URL, target: "_blank", active: false })}
            iconName="bookOpen"
            label="Handbuch"
          />
        </>
      )}

      {studentSmallTeam && (
        <Suspense fallback={null}>
          <UploadButton
            studentSmallTeam={studentSmallTeam}
            pathname={pathname}
            baseUrl={baseUrl}
          />
        </Suspense>
      )}

      <ToolbarButton
        id="leave-button"
        to={leaveTo}
        active={false}
        iconName="logOut"
        label="Verlassen"
      />
    </nav>
  );
};
